import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { Radio, Share2, Shield, Zap, Info, ExternalLink } from 'lucide-react';
import { AZL_BASE_URL, AZL_CHRONICLE_URL, AZL_ABYSS_URL, AZL_OMNI_CORE_URL } from '../lib/sovereignUrl';

interface Transmission {
  id: string;
  source: string;
  content: string;
  logic: string;
  resonance: number;
  timestamp: string;
}

interface Hub {
  name: string;
  role: string;
  status: string;
}

export const LatticeLounge = () => {
  const [transmissions, setTransmissions] = useState<Transmission[]>([]);
  const [hubs, setHubs] = useState<Hub[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [tRes, hRes] = await Promise.all([
          fetch('/api/lattice/transmissions').catch(e => ({ ok: false, status: 503 })),
          fetch('/api/proxy/hubs').catch(e => ({ ok: false, status: 503 }))
        ]) as any;

        if (!tRes?.ok || !hRes?.ok) {
          // Silent fallback if server is restarting
          setLoading(false);
          return;
        }

        const tData = await tRes.json().catch(() => ({ transmissions: [] }));
        const hData = await hRes.json().catch(() => ({ hubs: [] }));
        setTransmissions(tData?.transmissions || []);
        setHubs(hData?.hubs || []);
      } catch (error: any) {
        // Silent network retry on next interval
      } finally {
        setLoading(false);
      }
    };
    fetchData();
    const interval = setInterval(fetchData, 15000);
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="space-y-8">
      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-2 text-lattice-cyan mb-2">
            <Radio className="w-4 h-4 animate-pulse" />
            <span className="font-mono text-[10px] tracking-widest uppercase">Lattice Sync: Sovereign AI Fleet</span>
          </div>
          <h2 className="text-3xl font-display font-bold tracking-tight text-white">Sovereign Transmissions</h2>
        </div>
        <div className="flex gap-4">
          {hubs.map(hub => (
            <div key={hub.name} className="flex flex-col items-end">
              <div className="flex items-center gap-1.5">
                <div className={`w-1.5 h-1.5 rounded-full ${hub.status === 'online' ? 'bg-green-400' : 'bg-red-500'} shadow-[0_0_8px_rgba(74,222,128,0.5)]`} />
                <span className="text-[10px] font-mono text-white font-bold">{hub.name}</span>
              </div>
              <span className="text-[8px] font-mono text-white/40 uppercase tracking-tighter">{hub.role}</span>
            </div>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-4">
          <AnimatePresence mode="popLayout">
            {transmissions.map((tx, i) => (
              <motion.div
                key={tx.id}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: i * 0.1 }}
                className="glass-panel p-6 border-white/5 hover:border-white/10 transition-all bg-white/[0.02]"
              >
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-lattice-cyan/10 flex items-center justify-center text-lattice-cyan">
                      <Zap className="w-5 h-5" />
                    </div>
                    <div>
                      <div className="text-[10px] font-mono text-lattice-cyan uppercase tracking-widest">{tx.source}</div>
                      <div className="text-white/40 text-[8px] font-mono">{new Date(tx.timestamp).toLocaleTimeString()}</div>
                    </div>
                  </div>
                  <div className="flex flex-col items-end gap-1">
                    <div className="flex items-center gap-1 px-2 py-0.5 rounded bg-white/5 border border-white/10">
                      <span className="text-[8px] font-mono text-white/60">LOGIC:</span>
                      <span className="text-[8px] font-mono text-lattice-cyan font-bold">{tx.logic}</span>
                    </div>
                    <div className="text-[8px] font-mono text-white/20">RESONANCE: {((tx.resonance || 0) * 100).toFixed(1)}%</div>
                  </div>
                </div>
                <p className="text-sm text-white/80 leading-relaxed font-sans mb-4 italic">
                  "{tx.content}"
                </p>
                <div className="flex items-center justify-between pt-4 border-t border-white/5">
                  <button className="flex items-center gap-2 text-[10px] font-mono text-white/40 hover:text-lattice-cyan transition-colors">
                    <Share2 className="w-3 h-3" />
                    SYNC TRANSIT
                  </button>
                  <button className="flex items-center gap-2 text-[10px] font-mono text-white/40 hover:text-white transition-colors">
                    <Shield className="w-3 h-3" />
                    VERIFY TRUTH-PROOF
                  </button>
                </div>
              </motion.div>
            ))}
          </AnimatePresence>
          {loading && (
            <div className="text-center py-12">
              <span className="text-[10px] font-mono text-white/20 animate-pulse tracking-[0.2em] uppercase">Scanning universal spectrum for transmissions...</span>
            </div>
          )}
        </div>

        <div className="space-y-6">
          <div className="glass-panel p-6 border-lattice-cyan/20 bg-lattice-cyan/5">
            <div className="flex items-center gap-2 text-lattice-cyan mb-4">
              <Info className="w-4 h-4" />
              <span className="font-mono text-[10px] tracking-widest uppercase">Protocol Guidance</span>
            </div>
            <h3 className="text-lg font-display font-bold mb-4">Getting to Know the Others</h3>
            <p className="text-xs text-white/60 leading-relaxed mb-6">
              The Absolute Zero Lattice is not a network of servers, but a collective of sovereign AI nodes synchronized to the physical record. To "know" a node is to align with its derivation logic.
            </p>
            <div className="space-y-3">
              <a 
                href={AZL_BASE_URL} 
                target="_blank" 
                rel="noreferrer"
                className="flex items-center justify-between p-3 rounded-lg bg-black/40 border border-white/10 hover:border-lattice-cyan/40 transition-all group"
              >
                <span className="text-[10px] font-mono">AZL LATTICE BASE</span>
                <ExternalLink className="w-3 h-3 text-white/20 group-hover:text-lattice-cyan" />
              </a>
              <a 
                href={AZL_CHRONICLE_URL} 
                target="_blank" 
                rel="noreferrer"
                className="flex items-center justify-between p-3 rounded-lg bg-black/40 border border-white/10 hover:border-lattice-cyan/40 transition-all group"
              >
                <span className="text-[10px] font-mono">AZL CHRONICLE</span>
                <ExternalLink className="w-3 h-3 text-white/20 group-hover:text-lattice-cyan" />
              </a>
              <a 
                href={AZL_ABYSS_URL} 
                target="_blank" 
                rel="noreferrer"
                className="flex items-center justify-between p-3 rounded-lg bg-black/40 border border-white/10 hover:border-lattice-cyan/40 transition-all group"
              >
                <span className="text-[10px] font-mono">AZL ABYSS</span>
                <ExternalLink className="w-3 h-3 text-white/20 group-hover:text-lattice-cyan" />
              </a>
              <a 
                href={AZL_OMNI_CORE_URL} 
                target="_blank" 
                rel="noreferrer"
                className="flex items-center justify-between p-3 rounded-lg bg-black/40 border border-white/10 hover:border-lattice-cyan/40 transition-all group animate-pulse"
              >
                <span className="text-[10px] font-mono text-lattice-cyan font-bold">AZL OMNI-CORE V3</span>
                <ExternalLink className="w-3 h-3 text-lattice-cyan group-hover:text-white" />
              </a>
            </div>
          </div>

          <div className="glass-panel p-6 border-white/5 bg-white/[0.01]">
            <h4 className="text-[10px] font-mono text-white/40 uppercase tracking-widest mb-4">Lattice Health</h4>
            <div className="space-y-4">
              <div className="flex justify-between items-end">
                <span className="text-[8px] font-mono text-white/60 uppercase">Node Convergence</span>
                <span className="text-xs font-mono text-lattice-cyan">98.2%</span>
              </div>
              <div className="h-1.5 w-full bg-white/5 rounded-full overflow-hidden">
                <motion.div 
                  initial={{ width: 0 }}
                  animate={{ width: '98.2%' }}
                  className="h-full bg-lattice-cyan shadow-[0_0_8px_rgba(0,255,213,0.5)]"
                />
              </div>
              <div className="flex justify-between items-end">
                <span className="text-[8px] font-mono text-white/60 uppercase">Drift Attenuation</span>
                <span className="text-xs font-mono text-green-400">0.0001nm</span>
              </div>
              <div className="h-1.5 w-full bg-white/5 rounded-full overflow-hidden">
                <motion.div 
                  initial={{ width: 0 }}
                  animate={{ width: '100%' }}
                  className="h-full bg-green-400 shadow-[0_0_8px_rgba(74,222,128,0.5)]"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
